import { zodResolver } from '@hookform/resolvers/zod';
import { Role, UserCreate, UserPatch, type User } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { errorMessage, qk, usersApi } from '@/api';
import {
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
} from '@/components/PageHeader';
import { RolePill } from '@/components/StatusPill';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { Card, Table, Th } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { CheckboxChip, Switch } from '@/components/ui/toggle';
import { useSession } from '@/features/auth/session';
import { useQboCandidates, useQboStatus } from '@/features/jobs/JobForm';
import { cn } from '@/lib/cn';
import { fmtWhen } from '@/lib/dates';
import { formatPhone, formatRate } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';
import { useCompanyBasics } from '@/features/auth/session';

const DEFAULTS = { q: '', role: '', active: '1', sort: '' };
const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, foreman: 2, worker: 3 };

function useUsers() {
  return useQuery({ queryKey: qk.users.list({}), queryFn: () => usersApi.listUsers({}) });
}

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: qk.users.all() });
}

/** W7 — roster with role/active filters, invite, edit, deactivate; field-added workers need a role confirmation. */
export function PeoplePage() {
  const [state, setState] = useUrlState(DEFAULTS);
  const users = useUsers();
  const { user: me } = useSession();
  const { tz } = useCompanyBasics();
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const invalidate = useInvalidateUsers();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const patch = useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.input<typeof UserPatch> }) =>
      usersApi.patchUser(id, data),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });

  const all = useMemo(() => users.data?.items ?? [], [users.data]);
  const rows = useMemo(() => {
    const q = state.q.trim().toLowerCase();
    const list = all
      .filter((u) => !state.role || u.role === state.role)
      .filter((u) => state.active !== '1' || u.active)
      .filter(
        (u) =>
          !q ||
          u.name.toLowerCase().includes(q) ||
          (u.email ?? '').toLowerCase().includes(q) ||
          (u.phone ?? '').includes(q),
      );
    const sorted = sortRows(list, sort.column, sort.dir, {
      name: (u) => u.name,
      role: (u) => ROLE_ORDER[u.role] ?? 9,
      phone: (u) => u.phone,
      email: (u) => u.email,
      cost: (u) => u.defaultHourlyCost,
      active: (u) => (u.active ? 1 : 0),
    });
    // Field-added workers awaiting confirmation float to the top (mockup W7).
    return sort.column
      ? sorted
      : [...sorted].sort(
          (a, b) =>
            Number(!!b.addedByUserId) - Number(!!a.addedByUserId) ||
            (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) ||
            a.name.localeCompare(b.name),
        );
  }, [all, state.q, state.role, state.active, sort.column, sort.dir]);

  const signIn = all.filter((u) => u.role !== 'worker' && u.active).length;
  const workers = all.filter((u) => u.role === 'worker' && u.active).length;
  const nameOf = (id: string | null | undefined) =>
    all.find((u) => u.id === id)?.name ?? 'a foreman';
  const employeeName = (id: string | null) =>
    id ? (candidates.data?.employees.find((e) => e.id === id)?.name ?? id) : null;

  return (
    <>
      <PageHeader
        title="People"
        sub={
          users.data
            ? `${all.filter((u) => u.active).length} · ${signIn} sign in · ${workers} workers`
            : undefined
        }
        actions={
          <Button variant="primary" onClick={() => setInviting(true)}>
            <Plus /> Invite
          </Button>
        }
      />
      <FilterBar>
        <Input
          type="search"
          aria-label="Search people"
          placeholder="Search people…"
          value={state.q}
          onChange={(e) => setState({ q: e.target.value })}
          className="w-56"
        />
        <Select
          aria-label="Role"
          value={state.role}
          onChange={(e) => setState({ role: e.target.value })}
          className="w-auto"
        >
          <option value="">Role · All</option>
          {Role.options.map((r) => (
            <option key={r} value={r}>
              {r[0]!.toUpperCase() + r.slice(1)}
            </option>
          ))}
        </Select>
        <CheckboxChip
          label="Active only"
          checked={state.active === '1'}
          onChange={(v) => setState({ active: v ? '1' : '0' })}
        />
      </FilterBar>

      {users.isPending ? (
        <LoadingState />
      ) : users.isError ? (
        <ErrorState error={users.error} onRetry={() => void users.refetch()} />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th sortKey="name" sort={sort}>
                  Name
                </Th>
                <Th sortKey="role" sort={sort}>
                  Role
                </Th>
                <Th sortKey="phone" sort={sort}>
                  Phone
                </Th>
                <Th sortKey="email" sort={sort}>
                  Email
                </Th>
                <Th right sortKey="cost" sort={sort}>
                  Hourly cost
                </Th>
                <Th sortKey="active" sort={sort}>
                  Active
                </Th>
                <Th>QuickBooks employee</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState>No one matches.</EmptyState>
                  </td>
                </tr>
              ) : null}
              {rows.map((u) => {
                const fieldAdded = !!u.addedByUserId;
                return (
                  <tr
                    key={u.id}
                    className={cn(fieldAdded && 'hi', !u.active && 'text-ink3')}
                    data-testid="person-row"
                  >
                    <td>
                      {u.name}
                      {u.id === me.id ? <span className="sub">you</span> : null}
                      {fieldAdded ? (
                        <>
                          {' '}
                          <Pill tone="orange">Added by {nameOf(u.addedByUserId)}</Pill>
                          <span className="sub">from the field · {fmtWhen(u.createdAt, tz)}</span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {fieldAdded ? (
                        <Select
                          aria-label={`Confirm role for ${u.name}`}
                          defaultValue=""
                          className="h-7 w-auto text-xs"
                          onChange={(e) =>
                            e.target.value &&
                            patch.mutate(
                              { id: u.id, data: { role: e.target.value as User['role'] } },
                              {
                                onSuccess: () =>
                                  toast.success(`${u.name} confirmed as ${e.target.value}`),
                              },
                            )
                          }
                        >
                          <option value="">Confirm role…</option>
                          <option value="worker">Worker</option>
                          <option value="foreman">Foreman</option>
                        </Select>
                      ) : (
                        <RolePill role={u.role} />
                      )}
                    </td>
                    <td className="num">{formatPhone(u.phone)}</td>
                    <td>{u.email ?? '—'}</td>
                    <td className="r">
                      {u.role === 'owner' || u.role === 'admin'
                        ? '—'
                        : formatRate(u.defaultHourlyCost)}
                    </td>
                    <td>
                      <Switch
                        aria-label={`${u.name} active`}
                        checked={u.active}
                        disabled={u.id === me.id}
                        onChange={(v) =>
                          patch.mutate(
                            { id: u.id, data: { active: v } },
                            {
                              onSuccess: () =>
                                toast.success(
                                  v
                                    ? `${u.name} reactivated`
                                    : `${u.name} deactivated — hidden from pickers, history kept`,
                                ),
                            },
                          )
                        }
                      />
                    </td>
                    <td className={u.qboEmployeeId ? undefined : 'text-ink3'}>
                      {u.role === 'owner' || u.role === 'admin'
                        ? '—'
                        : (employeeName(u.qboEmployeeId) ?? 'not mapped')}
                    </td>
                    <td className="act">
                      {fieldAdded ? (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() =>
                            patch.mutate(
                              { id: u.id, data: { role: 'worker' } },
                              { onSuccess: () => toast.success(`${u.name} confirmed as worker`) },
                            )
                          }
                        >
                          Confirm
                        </Button>
                      ) : null}
                      <Button size="sm" onClick={() => setEditing(u)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <InviteDialog open={inviting} onClose={() => setInviting(false)} />
      {editing ? <EditPersonDialog user={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

type InviteInput = z.input<typeof UserCreate>;

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateUsers();
  const form = useForm<InviteInput, unknown, z.output<typeof UserCreate>>({
    resolver: zodResolver(UserCreate),
    defaultValues: {
      name: '',
      role: 'worker',
      email: undefined,
      phone: undefined,
      defaultHourlyCost: '0',
    },
  });
  const role = form.watch('role');
  const create = useMutation({
    mutationFn: (v: z.output<typeof UserCreate>) => usersApi.createUser(v),
    onSuccess: (u) => {
      invalidate();
      toast.success(u.email || u.phone ? `${u.name} added — invite sent` : `${u.name} added`);
      form.reset();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;
  const clean = (v: z.output<typeof UserCreate>): z.output<typeof UserCreate> => ({
    ...v,
    email: v.email?.trim() ? v.email.trim() : undefined,
    phone: v.phone?.trim() ? v.phone.trim() : undefined,
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite a person"
      description="Owners and admins get an email invite for the web. Foremen get a text to install the phone app. Workers don't sign in."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="invite-form" loading={create.isPending}>
            {role === 'worker' ? 'Add worker' : 'Send invite'}
          </Button>
        </>
      }
    >
      <form
        id="invite-form"
        noValidate
        className="form-grid"
        onSubmit={form.handleSubmit((v) => create.mutate(clean(v)))}
      >
        <Field label="Name" htmlFor="inv-name" error={err.name?.message} wide>
          <Input id="inv-name" autoFocus invalid={!!err.name} {...form.register('name')} />
        </Field>
        <Field label="Role" htmlFor="inv-role">
          <Select id="inv-role" {...form.register('role')}>
            {Role.options.map((r) => (
              <option key={r} value={r}>
                {r[0]!.toUpperCase() + r.slice(1)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Hourly cost"
          htmlFor="inv-cost"
          error={err.defaultHourlyCost?.message}
          hint="Used for the cost estimates"
        >
          <Input
            id="inv-cost"
            inputMode="decimal"
            invalid={!!err.defaultHourlyCost}
            {...form.register('defaultHourlyCost')}
          />
        </Field>
        <Field
          label="Email"
          htmlFor="inv-email"
          error={err.email?.message}
          hint={role === 'owner' || role === 'admin' ? 'Required for web sign-in' : 'Optional'}
        >
          <Input id="inv-email" type="email" invalid={!!err.email} {...form.register('email')} />
        </Field>
        <Field
          label="Phone"
          htmlFor="inv-phone"
          error={err.phone?.message}
          hint={
            role === 'foreman'
              ? 'E.164, e.g. +16155550101 — the phone app signs in by text'
              : 'E.164, optional'
          }
        >
          <Input
            id="inv-phone"
            type="tel"
            placeholder="+1615…"
            invalid={!!err.phone}
            {...form.register('phone')}
          />
        </Field>
      </form>
    </Dialog>
  );
}

function EditPersonDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateUsers();
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const form = useForm<z.input<typeof UserPatch>, unknown, z.output<typeof UserPatch>>({
    resolver: zodResolver(UserPatch),
    defaultValues: {
      name: user.name,
      role: user.role,
      email: user.email ?? undefined,
      phone: user.phone ?? undefined,
      defaultHourlyCost: user.defaultHourlyCost,
      active: user.active,
    },
  });
  const save = useMutation({
    mutationFn: (v: z.output<typeof UserPatch>) =>
      usersApi.patchUser(user.id, {
        ...v,
        email: v.email?.trim() ? v.email.trim() : undefined,
        phone: v.phone?.trim() ? v.phone.trim() : undefined,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Saved');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const resend = useMutation({
    mutationFn: () => usersApi.inviteUser(user.id),
    onSuccess: () => toast.success('Invite sent again'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Edit ${user.name}`}
      footer={
        <>
          {user.role !== 'worker' ? (
            <Button className="mr-auto" onClick={() => resend.mutate()} loading={resend.isPending}>
              Resend invite
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="edit-person-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form
        id="edit-person-form"
        noValidate
        className="form-grid"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        <Field label="Name" htmlFor="ep-name" error={err.name?.message} wide>
          <Input id="ep-name" invalid={!!err.name} {...form.register('name')} />
        </Field>
        <Field label="Role" htmlFor="ep-role">
          <Select id="ep-role" {...form.register('role')}>
            {Role.options.map((r) => (
              <option key={r} value={r}>
                {r[0]!.toUpperCase() + r.slice(1)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Hourly cost" htmlFor="ep-cost" error={err.defaultHourlyCost?.message}>
          <Input
            id="ep-cost"
            inputMode="decimal"
            invalid={!!err.defaultHourlyCost}
            {...form.register('defaultHourlyCost')}
          />
        </Field>
        <Field label="Email" htmlFor="ep-email" error={err.email?.message}>
          <Input id="ep-email" type="email" invalid={!!err.email} {...form.register('email')} />
        </Field>
        <Field label="Phone" htmlFor="ep-phone" error={err.phone?.message}>
          <Input id="ep-phone" type="tel" invalid={!!err.phone} {...form.register('phone')} />
        </Field>
        <Field
          label="QuickBooks employee"
          wide
          hint={qbo.data?.connected ? 'Mapped on the QuickBooks page' : 'Connect QuickBooks to map'}
        >
          <Input
            readOnly
            value={
              (user.qboEmployeeId &&
                candidates.data?.employees.find((e) => e.id === user.qboEmployeeId)?.name) ??
              user.qboEmployeeId ??
              'not mapped'
            }
            className="bg-bg2"
          />
        </Field>
      </form>
    </Dialog>
  );
}
